'use client'

import { useEffect, useId, useRef } from 'react'
import { useTranslations } from 'next-intl'
import { SheetPortal } from './SheetPortal'
import { compareWithApps, type ComparisonRowKey } from '@/lib/delivery/appComparison'

/**
 * "¿Por qué cobramos envío?" (spec 2026-09-03). Sheet de SOLO LECTURA: recibe
 * la comida del carrito y el envío real, y muestra. No escribe estado, no
 * cotiza, no toca el checkout. Abre encima del OrderSheet (z-60 > z-50).
 *
 * La tabla nunca juega en contra: `compareWithApps` decide si hay total y
 * ahorro (modo completo) o solo comida + tarifas (compacto).
 */
export interface WhyDeliveryFeeSheetProps {
  open: boolean
  accentColor: string
  /** Subtotal del carrito en centavos; 0 = sin carrito (se usa el ejemplo). */
  foodCents: number
  /** `heldQuote.feeAmountCents` tal cual; null si no hay cotización viva. */
  deliveryFeeCents: number | null
  onClose: () => void
}

const money = (cents: number) => `$${(cents / 100).toFixed(2)}`

const ROW_KEY: Record<ComparisonRowKey, 'rowFood' | 'rowService' | 'rowSmallOrder' | 'rowDelivery' | 'rowTotal'> = {
  food: 'rowFood',
  service: 'rowService',
  smallOrder: 'rowSmallOrder',
  delivery: 'rowDelivery',
  total: 'rowTotal',
}

export function WhyDeliveryFeeSheet({ open, accentColor, foodCents, deliveryFeeCents, onClose }: WhyDeliveryFeeSheetProps) {
  const t = useTranslations('public.onlineOrder.checkout.whyFee')
  const labelId = useId()
  const ctaRef = useRef<HTMLButtonElement | null>(null)
  const prevFocusRef = useRef<HTMLElement | null>(null)

  // Escape cierra. Solo mientras está abierto; el listener se va con el sheet.
  useEffect(() => {
    if (!open) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  // Foco: el CTA lo recibe al abrir (a mano, no con `autoFocus`: así se puede
  // capturar qué tenía el foco ANTES) y ese elemento previo lo recupera al
  // cerrar — sin esto el foco cae al <body>, detrás del checkout que sigue
  // abierto (revisión final F3).
  useEffect(() => {
    if (open) {
      prevFocusRef.current = document.activeElement as HTMLElement | null
      ctaRef.current?.focus()
      return () => {
        prevFocusRef.current?.focus?.()
        prevFocusRef.current = null
      }
    }
  }, [open])

  if (!open) return null

  const cmp = compareWithApps({ foodCents, deliveryFeeCents })
  const accent = accentColor || '#ea580c'
  const tint = `color-mix(in srgb, ${accent} 12%, white)`
  // Tinta oscura del acento para texto sobre el tinte: el acento crudo no llega a 4.5:1 (revisión final).
  const accentInk = `color-mix(in srgb, ${accent} 55%, black)`

  const beats = [
    { title: t('beat1Title'), body: t('beat1Body') },
    { title: t('beat2Title'), body: t('beat2Body') },
    { title: t('beat3Title'), body: t('beat3Body') },
  ]

  return (
    <SheetPortal>
      <div
        className="fixed inset-0 z-[60] bg-black/50 flex items-end sm:items-center justify-center"
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelId}
        onClick={onClose}
      >
        <div
          className="relative w-full max-w-md bg-white text-gray-900 rounded-t-3xl sm:rounded-3xl shadow-2xl px-5 pt-3 pb-5"
          style={{ maxHeight: '92dvh', overflowY: 'auto' }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex justify-center pb-3">
            <div className="w-10 h-1.5 rounded-full bg-gray-300" />
          </div>

          <p id={labelId} className="text-[11px] font-bold uppercase tracking-wider text-gray-500">
            {t('label')}
          </p>
          <h2 className="mt-1 text-[22px] leading-tight font-extrabold tracking-[-0.02em] text-gray-900">
            {t('heroPre')}{' '}<span style={{ color: accent }}>{t('heroAccent')}</span>
          </h2>

          <ul className="mt-4 space-y-2.5">
            {beats.map((b, i) => (
              <li key={i} className="flex items-start gap-2.5">
                <span
                  className="mt-0.5 w-[22px] h-[22px] rounded-md flex items-center justify-center text-xs font-extrabold flex-shrink-0"
                  style={{ background: tint, color: accentInk }}
                >
                  {i + 1}
                </span>
                <p className="text-[13.5px] leading-snug">
                  <span className="font-bold">{b.title}</span> <span className="text-gray-500">{b.body}</span>
                </p>
              </li>
            ))}
          </ul>

          <div className="mt-4 rounded-2xl border border-gray-200 overflow-hidden">
            <div className="bg-gray-50 px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-gray-500">
              {cmp.example ? t('cardTitleExample', { amount: money(cmp.foodCents) }) : t('cardTitle', { amount: money(cmp.foodCents) })}
            </div>
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-[11px] font-semibold text-gray-500">
                  <th className="text-left px-3 pt-2 pb-1 font-semibold" scope="col" />
                  <th className="text-right px-3 pt-2 pb-1 font-semibold" scope="col">{t('colApp')}</th>
                  <th className="text-right px-3 pt-2 pb-1 font-semibold" scope="col">{t('colHere')}</th>
                </tr>
              </thead>
              <tbody>
                {cmp.rows.map((r) => {
                  const total = r.key === 'total'
                  return (
                    <tr key={r.key} className={total ? 'font-extrabold' : ''}>
                      <td className={`px-3 py-1.5 border-t ${total ? 'border-gray-300' : 'border-gray-100'}`}>{t(ROW_KEY[r.key])}</td>
                      <td className={`px-3 py-1.5 border-t text-right tabular-nums text-orange-900 ${total ? 'border-gray-300' : 'border-gray-100'}`}>{money(r.appCents)}</td>
                      <td className={`px-3 py-1.5 border-t text-right tabular-nums font-semibold text-green-900 ${total ? 'border-gray-300' : 'border-gray-100'}`}>{money(r.hereCents)}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
            <p className="px-3 pt-1.5 pb-2.5 text-[11px] leading-snug text-gray-400">
              {t('noteTypical')}{' '}
              {cmp.mode === 'full'
                ? t('noteNoTip')
                : cmp.deliveryFeeCents == null
                  ? t('noteRealDeliveryPending')
                  : t('noteRealDelivery', { amount: money(cmp.deliveryFeeCents) })}
            </p>
          </div>

          {cmp.mode === 'full' && cmp.savingsCents != null ? (
            <div className="mt-3 rounded-xl px-3 py-2.5 text-center text-[13px] font-bold" style={{ background: tint, color: accentInk }}>
              {t('savings', { amount: money(cmp.savingsCents) })}
            </div>
          ) : (
            <div className="mt-3 rounded-xl bg-gray-50 px-3 py-2.5 text-center text-[12.5px] leading-snug text-gray-700">
              {t('compactLine')}
            </div>
          )}

          <button
            type="button"
            ref={ctaRef}
            onClick={onClose}
            className="mt-4 w-full rounded-full py-3.5 text-white text-sm font-bold transition hover:opacity-95 active:scale-[0.99]"
            style={{ background: accent }}
          >
            {t('cta')}
          </button>
        </div>
      </div>
    </SheetPortal>
  )
}
